import { Link, useLocation } from "react-router-dom";
import { useIsAdmin, UserButton, CompanyDropdown } from "@x-veloper/dataslot-auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faChartLine } from "@fortawesome/free-solid-svg-icons";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";

export interface MenuItem {
  title: string;
  icon?: any;
  url?: string;
  items?: MenuItem[];
  adminOnly?: boolean;
  requiredPermissions?: string[];
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  menuItems: MenuItem[];
}

export default function Sidebar({ isOpen, onClose, menuItems }: SidebarProps) {
  const location = useLocation();
  const isAdmin = useIsAdmin();

  const isActive = (url: string) => location.pathname === url;

  /**
   * Filter menu items based on user role and permissions
   */
  const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
    return items.filter((item) => {
      // Check adminOnly flag
      if (item.adminOnly && !isAdmin) {
        return false;
      }
      return true;
    });
  };

  return (
    <aside
      className={`fixed top-0 bottom-0 left-0 z-50 w-64 bg-white shadow-lg transition-transform duration-300 lg:static lg:h-full lg:flex-shrink-0 lg:translate-x-0 ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="flex h-full flex-col">
        {/* Sidebar Header */}
        <div className="flex items-center justify-between border-b px-4 py-4">
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center">
              <FontAwesomeIcon icon={faChartLine} className="text-sm" />
            </div>
            <span className="font-semibold text-lg">Commission</span>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        {/* Company Dropdown */}
        <div className="border-b px-4 py-3">
          <CompanyDropdown
            components={{
              Badge,
              Button,
              Input,
              Skeleton,
              DropdownMenu,
              DropdownMenuContent,
              DropdownMenuItem,
              DropdownMenuLabel,
              DropdownMenuTrigger,
            }}
          />
        </div>

        {/* Sidebar Content */}
        <nav className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.title}>
                {item.items ? (
                  // Submenu
                  <div>
                    <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700">
                      {item.icon && (
                        <FontAwesomeIcon
                          icon={item.icon}
                          className="text-gray-500 w-4"
                        />
                      )}
                      <span>{item.title}</span>
                    </div>
                    <ul className="ml-6 mt-1 space-y-1">
                      {filterMenuItems(item.items).map((subItem) => (
                        <li key={subItem.title}>
                          <Link
                            to={subItem.url!}
                            onClick={onClose}
                            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                              isActive(subItem.url!)
                                ? "bg-blue-50 text-blue-600"
                                : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                            }`}
                          >
                            {subItem.icon && (
                              <FontAwesomeIcon
                                icon={subItem.icon}
                                className="w-3"
                              />
                            )}
                            <span>{subItem.title}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  // Single menu item
                  <Link
                    to={item.url!}
                    onClick={onClose}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive(item.url!)
                        ? "bg-blue-50 text-blue-600"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    {item.icon && (
                      <FontAwesomeIcon icon={item.icon} className="w-4" />
                    )}
                    <span>{item.title}</span>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Sidebar Footer with User Button */}
        <div className="border-t p-4">
          <UserButton
            components={{
              Avatar,
              AvatarFallback,
              AvatarImage,
              Badge,
              DropdownMenu,
              DropdownMenuContent,
              DropdownMenuItem,
              DropdownMenuLabel,
              DropdownMenuSeparator,
              DropdownMenuTrigger,
            }}
          />
        </div>
      </div>
    </aside>
  );
}
